import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem,
  Button,
  IconButton,
  Divider,
  Autocomplete,
} from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";

const IVA_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 15, label: "15%" },
  { value: 999, label: "No objeto de IVA" },
];

const norm = (t) =>
  (t || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const EditProduct = ({ product, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [barcode, setBarcode] = useState("");
  const [codigoAux, setCodigoAux] = useState("");
  const [seriado, setSeriado] = useState(false);
  const [pvp1, setPvp1] = useState("");
  const [ivaPorc, setIvaPorc] = useState(15);
  const [stockMin, setStockMin] = useState("");
  const [estado, setEstado] = useState(true);
  const [ecommerce, setEcommerce] = useState(true);
  const [description, setDescription] = useState("");
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const [sections, setSections] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  const [sectionId, setSectionId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");

  const [sectionNameSeed, setSectionNameSeed] = useState("");
  const [categoryNameSeed, setCategoryNameSeed] = useState("");
  const [brandNameSeed, setBrandNameSeed] = useState("");

  const fetchSections = async () => {
    const r = await fetch("/categorias/secciones/", { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const arr = Array.isArray(d) ? d : [];
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" }));
      setSections(arr);
    }
  };

  const fetchBrands = async () => {
    const r = await fetch("/categorias/marcas/", { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const arr = Array.isArray(d) ? d : [];
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" }));
      setBrands(arr);
    }
  };

  const fetchCategoriesBySection = async (sid) => {
    if (!sid) {
      setCategories([]);
      return;
    }
    const r = await fetch(`/categorias/seccion/${sid}/`, { credentials: "include" });
    const d = await r.json();
    if (r.ok) {
      const arr = Array.isArray(d) ? d : [];
      arr.sort((a, b) => (a?.name || "").localeCompare(b?.name || "", "es", { sensitivity: "base" }));
      setCategories(arr);
    }
  };

  useEffect(() => {
    fetchSections();
    fetchBrands();
  }, []);

  useEffect(() => {
    if (!product) return;

    setName(product.name || "");
    setCode(product.code || "");
    setBarcode(product.barcode || "");
    setCodigoAux(product.codigo_auxiliar || "");
    setSeriado(!!product.seriado);
    setPvp1(String(product.pvp1 ?? ""));
    setIvaPorc(product.iva_porcentaje ?? 15);
    setStockMin(product.stock_minimo ?? "");
    setEstado(product.estado === "activo");
    setEcommerce(product.ecommerce !== undefined ? product.ecommerce : true);
    setDescription(product.description || "");
    setExistingImages(product.images || []);
    setNewImages([]);

    const rawSectionId = product.section_id ?? product.section ?? "";
    const rawCategoryId = product.category_id ?? product.category ?? "";
    const rawBrandId = product.brand_id ?? product.brand ?? "";

    const sIdIsNumeric = rawSectionId !== "" && !Number.isNaN(Number(rawSectionId));
    const cIdIsNumeric = rawCategoryId !== "" && !Number.isNaN(Number(rawCategoryId));
    const bIdIsNumeric = rawBrandId !== "" && !Number.isNaN(Number(rawBrandId));

    setSectionId(sIdIsNumeric ? String(rawSectionId) : "");
    setCategoryId(cIdIsNumeric ? String(rawCategoryId) : "");
    setBrandId(bIdIsNumeric ? String(rawBrandId) : "");

    setSectionNameSeed(!sIdIsNumeric ? String(rawSectionId || "") : "");
    setCategoryNameSeed(!cIdIsNumeric ? String(rawCategoryId || "") : "");
    setBrandNameSeed(!bIdIsNumeric ? String(rawBrandId || "") : "");

    if (sIdIsNumeric) fetchCategoriesBySection(String(rawSectionId));
  }, [product]);

  useEffect(() => {
    if (!sectionId) return;
    fetchCategoriesBySection(sectionId);
  }, [sectionId]);

  useEffect(() => {
    if (!sectionId || !sectionNameSeed || sections.length === 0) return;
    if (sectionId) return;
    const found = sections.find((s) => norm(s.name) === norm(sectionNameSeed));
    if (found) setSectionId(String(found.id));
  }, [sections, sectionNameSeed, sectionId]);

  useEffect(() => {
    if (!brandNameSeed || brands.length === 0) return;
    if (brandId) return;
    const found = brands.find((b) => norm(b.name) === norm(brandNameSeed));
    if (found) setBrandId(String(found.id));
  }, [brands, brandNameSeed, brandId]);

  useEffect(() => {
    if (!categoryNameSeed || categories.length === 0) return;
    if (categoryId) return;
    const found = categories.find((c) => norm(c.name) === norm(categoryNameSeed));
    if (found) setCategoryId(String(found.id));
  }, [categories, categoryNameSeed, categoryId]);

  useEffect(() => {
    if (!sectionId || categories.length === 0) return;
    if (!categoryId) return;
    const ok = categories.some((c) => String(c.id) === String(categoryId));
    if (!ok) setCategoryId("");
  }, [categories, sectionId, categoryId]);

  const ivaValor = useMemo(() => {
    const base = parseFloat(pvp1 || 0);
    if (ivaPorc === 999) return "0.00";
    return (base * (ivaPorc / 100)).toFixed(2);
  }, [pvp1, ivaPorc]);

  const precioFinal = useMemo(() => {
    const base = parseFloat(pvp1 || 0);
    const iva = parseFloat(ivaValor || 0);
    return (base + iva).toFixed(2);
  }, [pvp1, ivaValor]);

  const removeExistingImage = (img) => {
    setExistingImages((prev) => prev.filter((i) => i !== img));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setNewImages((prev) => [...prev, ...files]);
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const payload = {
      name,
      barcode,
      codigo_auxiliar: codigoAux,
      seriado,
      section: sectionId,
      category: categoryId,
      brand: brandId,
      pvp1,
      iva_porcentaje: ivaPorc,
      stock_minimo: stockMin,
      estado: estado ? "activo" : "inactivo",
      ecommerce,
      description,
      existingImages,
      newImages,
    };
    onSubmit(payload);
  };

  const selectedSection = useMemo(
    () => sections.find((s) => String(s.id) === String(sectionId)) || null,
    [sections, sectionId]
  );

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(categoryId)) || null,
    [categories, categoryId]
  );

  const selectedBrand = useMemo(
    () => brands.find((b) => String(b.id) === String(brandId)) || null,
    [brands, brandId]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" mb={2}>
        Editar Producto
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField fullWidth label="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
        </Grid>

        <Grid item xs={6}>
          <TextField fullWidth label="Código" value={code} InputProps={{ readOnly: true }} />
        </Grid>

        <Grid item xs={6}>
          <TextField fullWidth label="Código de barras" value={barcode} onChange={(e) => setBarcode(e.target.value)} />
        </Grid>

        <Grid item xs={6}>
          <TextField fullWidth label="Código auxiliar" value={codigoAux} onChange={(e) => setCodigoAux(e.target.value)} />
        </Grid>

        <Grid item xs={6}>
          <FormControlLabel
            control={<Switch checked={seriado} onChange={(e) => setSeriado(e.target.checked)} />}
            label="Seriado"
          />
        </Grid>

        <Grid item xs={4}>
          <Autocomplete
            options={sections}
            value={selectedSection}
            onChange={(_, v) => {
              const id = v ? String(v.id) : "";
              setSectionId(id);
              setCategoryId("");
              setCategoryNameSeed("");
            }}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
            renderInput={(params) => <TextField {...params} fullWidth label="Sección" />}
          />
        </Grid>

        <Grid item xs={4}>
          <Autocomplete
            options={categories}
            value={selectedCategory}
            onChange={(_, v) => setCategoryId(v ? String(v.id) : "")}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
            renderInput={(params) => <TextField {...params} fullWidth label="Categoría" />}
            disabled={!sectionId}
          />
        </Grid>

        <Grid item xs={4}>
          <Autocomplete
            options={brands}
            value={selectedBrand}
            onChange={(_, v) => setBrandId(v ? String(v.id) : "")}
            getOptionLabel={(opt) => opt?.name || ""}
            isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
            renderInput={(params) => <TextField {...params} fullWidth label="Marca" />}
          />
        </Grid>

        <Grid item xs={6}>
          <TextField fullWidth label="PVP1" value={pvp1} onChange={(e) => setPvp1(e.target.value)} />
        </Grid>

        <Grid item xs={6}>
          <TextField
            select
            fullWidth
            label="IVA %"
            value={ivaPorc}
            onChange={(e) => setIvaPorc(parseInt(e.target.value, 10))}
          >
            {IVA_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6}>
          <TextField fullWidth label="IVA $" value={ivaValor} InputProps={{ readOnly: true }} />
        </Grid>

        <Grid item xs={6}>
          <TextField fullWidth label="Precio Final" value={precioFinal} InputProps={{ readOnly: true }} />
        </Grid>

        <Grid item xs={6}>
          <TextField
            fullWidth
            type="number"
            label="Stock mínimo"
            value={stockMin}
            onChange={(e) => setStockMin(e.target.value)}
          />
        </Grid>

        <Grid item xs={6}>
          <FormControlLabel control={<Switch checked={estado} onChange={(e) => setEstado(e.target.checked)} />} label="Activo" />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={<Switch checked={ecommerce} onChange={(e) => setEcommerce(e.target.checked)} />}
            label={ecommerce ? "Visible en e-commerce" : "No visible en e-commerce"}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2">Imágenes actuales:</Typography>
          <Grid container spacing={1}>
            {existingImages.map((img, i) => (
              <Grid item xs={3} key={i}>
                <Box sx={{ position: "relative", width: "100%", aspectRatio: "1" }}>
                  <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeExistingImage(img)}
                    sx={{ position: "absolute", top: 0, right: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2">Nuevas imágenes:</Typography>
          <Grid container spacing={1}>
            {newImages.map((file, i) => (
              <Grid item xs={3} key={i}>
                <Box sx={{ position: "relative", width: "100%", aspectRatio: "1" }}>
                  <img src={URL.createObjectURL(file)} alt="" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeNewImage(i)}
                    sx={{ position: "absolute", top: 0, right: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Button variant="contained" fullWidth startIcon={<AddPhotoAlternateIcon />} component="label" sx={{ mb: 2 }}>
            Agregar Imágenes
            <input type="file" hidden accept="image/*" multiple onChange={handleImageUpload} />
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ textAlign: "right", mt: 2, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!sectionId || !categoryId || !brandId}>
          Guardar Cambios
        </Button>
      </Box>
    </Box>
  );
};

export default EditProduct;
